import React, { useCallback, useMemo, useRef, useState } from "react";
import type { Api } from "../../services/api";

export type ResponsiveImageSizes = NonNullable<
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"]["images"][number]["sizes"]
>;

interface ResponsiveImageProps {
  image?: {
    url: string;
    path: string;
    name?: string | null;
    legend?: string | null;
    sizes?: ResponsiveImageSizes | null;
  } | null;
  className?: string;
  alt?: string;
  sizes?: string;
  loading?: "lazy" | "eager";
  style?: React.CSSProperties;
  Component?: React.ElementType;
}

const sizeOrder = [
  "icon",
  "thumbnail",
  "small",
  "medium",
  "large",
] as const satisfies readonly (keyof ResponsiveImageSizes)[];

const fallbackSizeOrder = [
  "large",
  "medium",
  "small",
  "thumbnail",
  "icon",
] as const satisfies readonly (keyof ResponsiveImageSizes)[];

function StyledImage({
  $isLoading,
  className,
  style,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & { $isLoading?: boolean }) {
  return (
    <img
      className={className}
      style={{
        opacity: $isLoading ? 0 : 1,
        transition: "opacity 0.3s ease-in-out",
        width: "100%",
        height: "100%",
        ...style,
      }}
      {...props}
    />
  );
}

const ResponsiveImage = ({
  image,
  className,
  alt,
  sizes: sizesProp = "100vw",
  loading = "lazy",
  style,
  Component = StyledImage,
}: ResponsiveImageProps) => {
  const [isLoading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const fallbackIndexRef = useRef(0);
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(undefined);

  const fallbackUrls = useMemo(() => {
    if (!image?.sizes) return [];
    const urls: string[] = [];
    for (const size of fallbackSizeOrder) {
      if (size in image.sizes) {
        urls.push(image.sizes[size].file);
      }
    }
    return urls;
  }, [image?.sizes]);

  const primarySrc = image?.sizes?.original.file ?? image?.url;

  const resolvedSrc = currentSrc ?? primarySrc;

  const onImageLoad = useCallback(() => {
    setLoading(false);
    setHasError(false);
  }, []);

  const onImageError = useCallback(() => {
    const nextIndex = fallbackIndexRef.current;
    if (nextIndex < fallbackUrls.length) {
      fallbackIndexRef.current = nextIndex + 1;
      setCurrentSrc(fallbackUrls[nextIndex]);
    } else {
      setHasError(true);
      setLoading(false);
    }
  }, [fallbackUrls]);

  if (!image) {
    return null;
  }

  const { sizes, legend } = image;
  const srcSet =
    currentSrc === undefined && sizes != null
      ? sizeOrder
          .filter((size) => size in sizes)
          .map((size) => `${sizes[size].file} ${sizes[size].width}w`)
          .join(", ")
      : undefined;
  const isFallbackVisible =
    loading === "lazy" && (isLoading || resolvedSrc === undefined || hasError);

  return (
    <>
      {resolvedSrc !== undefined && !hasError && (
        <Component
          className={className}
          src={resolvedSrc}
          srcSet={srcSet}
          sizes={loading === "lazy" ? `auto, ${sizesProp}` : sizesProp}
          alt={alt ?? legend ?? ""}
          loading={loading}
          $isLoading={isLoading}
          onLoad={onImageLoad}
          onError={onImageError}
          style={style}
        />
      )}
      {isFallbackVisible && (
        <div
          className={`responsive-img-fallback animate-pulse bg-[#e5e5e5] size-full  rounded-xl ${className ?? ""}`}
          style={style}
        />
      )}
    </>
  );
};

export default ResponsiveImage;
